import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { ToastrHelper } from '../../../helpers/toastr.helper';
import { CollaborationPlatformTypeEnum } from '../../../models/projects/collaboration-platform-type.enum.model';
import { ProjectAttendee } from '../../../models/projects/project-attendee.model';
import { Project } from '../../../models/projects/project.model';
import { Attendee } from '../../../models/shared/attendee.model';
import { AccountsService } from '../../../services/identity/accounts.service';
import { ProfileService } from '../../../services/identity/profile.service';
import { ProjectsService } from '../../../services/projects/projects.service';
import { ImageService } from '../../../services/shared/image.service';
import { ModifierTypes, User } from './../../../models/identity/user.model';

@Component({
    selector: 'app-project-details',
    templateUrl: './project-details.component.html',
    styleUrls: ['./project-details.component.scss'],
})
export class ProjectDetailsComponent implements OnInit {
    public attendeesDialog: boolean;
    public attendeesExceptOwner: Array<Attendee>;
    public attendeesMailTo: string;
    public canUserUpdateProjectMembers = false;
    public cancelJoinProjectDialog: boolean;
    public closeProjectDialog: boolean;
    public collaborationPlatformTypeEnum = CollaborationPlatformTypeEnum;
    public dictionary = environment.dictionary;
    public isAModifier = ModifierTypes.None;
    public isCollaborationPlatformAsyncCreation = false;
    public isCommunityManager = false;
    public isConfirmedAttendee = false;
    public isMember = false;
    public isOwner = false;
    public isPendingAttendee = false;
    public isPlatformAdmin = false;
    public joinProjectDialog: boolean;
    public leaveProjectDialog: boolean;
    public modifierList: User[] = [];
    public modifierListReady = false;
    public modifierTypes = ModifierTypes;
    public project: Project;
    public projectLoaded: Promise<boolean>;
    public projectOwner: Attendee;
    public waitingAttendeesDialog: boolean;
    public deleteProjectDialog: boolean;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly profileService: ProfileService,
        private readonly projectsService: ProjectsService,
        public location: Location,
        public accountsService: AccountsService,
        public imageService: ImageService,
        private sanitizer: DomSanitizer,
        private translate: TranslateService,
        private router: Router,
        private readonly toastrHelper: ToastrHelper,
    ) {}

    public get canViewCollaborativePlatform() {
        const res = (this.isMember || this.isOwner) && this.project.collaborationPlatformType !== CollaborationPlatformTypeEnum.None;
        return res;
    }

    contactMyTeamClick() {
        window.location.href = `mailto:${this.attendeesMailTo}`;
    }

    public closeAttendeesModal() {
        this.attendeesDialog = false;
    }

    public closeCancelJoinProjectDialog() {
        this.cancelJoinProjectDialog = false;
    }

    public closeCloseProjectModal() {
        this.closeProjectDialog = false;
    }

    public closeJoinProjectDialog() {
        this.joinProjectDialog = false;
    }

    public closeLeaveProjectDialog() {
        this.leaveProjectDialog = false;
    }

    public closeWaitingAttendeesDialog() {
        this.waitingAttendeesDialog = false;
    }

    public getCanUserUpdateProjectMembers(project: Project) {
        return this.isPlatformAdmin || (this.isCommunityManager && !project.allCommunitiesVisibility);
    }

    public getModifierFullNameById(id: string): string {
        const user: User = this.modifierList.find((modifier) => modifier.userId == id);
        if (!user) return '';

        const userTypeLabels = {
            isCurrentUser: this.accountsService.currentUser.userId === user.userId,
            isProjectOwner: this.projectOwner.id === user.userId,
            isAdmin: user.userType?.libelle === 'Administrateur',
            isOrgManager:
                user.organization?.id === this.project.organization?.id &&
                user.organization?.roles?.some((role) => role.libelle === "Gestionnaire d'organisation"),
            isCommunityManager: user.communities?.some(
                (userCommunity) =>
                    this.project.communities.some((projectCommunity) => projectCommunity.id === userCommunity.id) &&
                    userCommunity.roles?.some((role) => role.libelle === 'Gestionnaire de communauté'),
            ),
        };

        let userTypeLibelle: string;
        if (userTypeLabels.isCurrentUser) {
            userTypeLibelle = this.translate.instant('projects.pages.details.userRolesLabels.you');
        } else if (userTypeLabels.isProjectOwner) {
            userTypeLibelle = this.translate.instant('projects.pages.details.userRolesLabels.projectOwner');
        } else if (userTypeLabels.isAdmin) {
            userTypeLibelle = this.translate.instant('projects.pages.details.userRolesLabels.administrator');
        } else if (userTypeLabels.isOrgManager) {
            userTypeLibelle = this.translate.instant('projects.pages.details.userRolesLabels.organizationManager');
        } else if (userTypeLabels.isCommunityManager) {
            userTypeLibelle = this.translate.instant('projects.pages.details.userRolesLabels.communityManager');
        }

        return `${user.firstName} ${user.lastName} (${userTypeLibelle})`;
    }

    public hookOpenCollaborationPlatform() {
        this.openCollaborationPlatform();
    }

    public isMemberOrOwnerOfProject() {
        return this.isMember || this.isOwner || this.canUserUpdateProjectMembers;
    }

    copyProjectUrl() {
        const url = window.location.href;
        navigator.clipboard.writeText(url).then(() => {
            const msg = 'Lien copié dans le presse-papier';
            this.toastrHelper.showSuccess(msg);
            return false;
        });
    }

    public loadProject(projectId: string) {
        this.projectsService.getProjectById(projectId).subscribe(
            (project) => {
                this.project = project;
                this.isPendingAttendee = project.pendingAttendees?.some((attendee) => attendee.id === this.accountsService.currentUser.userId);

                this.LoadAnonymizedProject(project);

                this.isAModifier = this.accountsService.canModifyProject(project);
                this.projectLoaded = Promise.resolve(true);
                this.isCollaborationPlatformAsyncCreation = PROJECT_COLLABORATION_PLATFORM_ASYNC_CREATION_TYPES.includes(
                    this.project.collaborationPlatformType,
                );

                this.isCommunityManager = this.accountsService.isUserCommunityManagerOfAtLeastOneCommunity(project.communities.map((c) => c.id));
                this.canUserUpdateProjectMembers = this.getCanUserUpdateProjectMembers(project);

                this.loadModificationsHistory(project);
            },
            (error) => {
                this.router.navigateByUrl('/projects');
            },
        );
    }

    public modifyProject() {
        if (this.isOwner) {
            this.router.navigate(['/profile', 'my-projects', this.project.id]);
        } else if (this.isAModifier == this.modifierTypes.Admin) {
            this.router.navigate(['/administration', 'projects', this.project.id]);
        } else if (this.isAModifier == this.modifierTypes.CommunityManager) {
            this.router.navigate(['/community', 'projects', this.project.id]);
        } else {
            this.router.navigate(['/administration-organizations', 'projects', this.project.id]);
        }
    }

    public ngOnInit() {
        this.route.paramMap.subscribe((params) => this.loadProject(params.get('id')));
        this.isPlatformAdmin = this.accountsService.isAdmin();
    }

    public openCancelJoinProjectDialog() {
        this.cancelJoinProjectDialog = true;
    }

    public openCloseProjectModal() {
        this.closeProjectDialog = true;
    }

    public openDeleteProjectDialog() {
        this.deleteProjectDialog = true;
    }

    public closeDeleteProjectDialog() {
        this.deleteProjectDialog = false;
    }

    public openJoinProjectDialog() {
        this.joinProjectDialog = true;
    }

    public openLeaveProjectDialog() {
        this.leaveProjectDialog = true;
    }

    public showAttendeesDialog() {
        this.attendeesDialog = true;
    }

    public showWaitingAttendeesDialog() {
        this.waitingAttendeesDialog = true;
    }

    public userCancelJoinedProject() {
        this.isMember = false;
        this.isConfirmedAttendee = false;
        this.isPendingAttendee = false;
    }

    public userJoinedProject() {
        this.isPendingAttendee = true;
        this.isMember = false;
        this.isConfirmedAttendee = false;
    }

    public userLeftProject() {
        this.isMember = false;
        this.isConfirmedAttendee = false;
        this.isPendingAttendee = false;
    }

    private LoadAnonymizedProject(project: Project) {
        if (!project.isAnonymized) {
            this.project.attendees.sort((a, b) => (a.isOwner < b.isOwner ? 1 : -1));
            this.profileService
                .getMultipleProfiles(project.pendingAttendees.map((pendingAttendee) => pendingAttendee.id))
                .subscribe((profiles: User[]) => {
                    project.pendingAttendees.forEach((pendingAttendee: ProjectAttendee) => {
                        const profile = profiles.filter((profile) => profile.userId == pendingAttendee.id)[0];
                        // Refresh data with profile in case of edit.
                        pendingAttendee.firstName = profile.firstName;
                        pendingAttendee.lastName = profile.lastName;
                        pendingAttendee.email = profile.email;
                        pendingAttendee.profilePicture = profile.pictureReference;
                        pendingAttendee.organizationName = profile.organization.name;
                        pendingAttendee.jobTitle = profile.jobTitle;
                        pendingAttendee.safeMotivation = this.sanitizer.bypassSecurityTrustHtml(pendingAttendee.motivation);
                    });
                });
            this.projectOwner = project?.owner;
            this.profileService.getMultipleProfiles(project.attendees.map((attendee) => attendee.id)).subscribe((profiles: User[]) => {
                project.attendees.forEach((attendee: Attendee) => {
                    const profile = profiles.filter((profile) => profile.userId == attendee.id)[0];
                    // Refresh data with profile in case of edit.
                    attendee.firstName = profile.firstName;
                    attendee.lastName = profile.lastName;
                    attendee.email = profile.email;
                    attendee.profilePicture = profile.pictureReference;
                    attendee.organizationName = profile.organization.name;
                    attendee.jobTitle = profile.jobTitle;
                });
                this.projectOwner.jobTitle = profiles?.filter((profile) => profile.userId == this.projectOwner.id)[0]?.jobTitle;
            });

            this.isOwner = this.projectOwner.id === this.accountsService.currentUser.userId;
            this.isMember = this.isOwner || project.attendees.some((attendee) => attendee.id === this.accountsService.currentUser.userId);
            this.attendeesExceptOwner = project.attendees?.filter((attendee) => !attendee.isOwner);
            this.attendeesMailTo = (this.attendeesExceptOwner.map((attendee) => attendee.email) || []).join(',');
            this.isConfirmedAttendee = this.isOwner || project.attendees.some((attendee) => attendee.id === this.accountsService.currentUser.userId);
        }
    }

    private loadModificationsHistory(project: Project) {
        if (this.isOwner || this.isAModifier == ModifierTypes.Admin) {
            project.updates?.forEach((update) => {
                if (!this.modifierList.some((modifier) => modifier.userId == update.modifierId)) {
                    if (update.modifierId == this.accountsService.currentUser.userId) this.modifierList.push(this.accountsService.currentUser);
                    else if (update.modifierId == this.projectOwner.id)
                        this.modifierList.push(
                            Object.assign(new User(), {
                                userId: this.projectOwner.id,
                                firstName: this.projectOwner.firstName,
                                lastName: this.projectOwner.lastName,
                            }),
                        );
                    else
                        this.profileService.getUserProfileById(update.modifierId).subscribe((modifier) => {
                            this.modifierList.push(modifier);
                        });
                }
            });
            if (this.modifierList != null && this.modifierList.length > 0) {
                this.modifierListReady = true;
            }
        }
    }

    private openCollaborationPlatform() {
        window.open(this.project.collaborationPlatformUrl, '_blank');
    }
}

const PROJECT_COLLABORATION_PLATFORM_ASYNC_CREATION_TYPES: Array<CollaborationPlatformTypeEnum> = [
    CollaborationPlatformTypeEnum.WuDo,
    CollaborationPlatformTypeEnum.Club,
];
